import React, { PropsWithChildren, useMemo } from 'react';

type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];

import { Skeleton } from '@material-ui/lab';
import { useCompanyLoader } from 'services/company-dataloader.service';
import LanguageIcon from '@material-ui/icons/Language';
import CategoryIcon from '@material-ui/icons/Category';
import { EntityNameColumn } from '@components/entity-name-column';
import { windowObj, useDefaultImage } from '@lib/common';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';

export const toCamelCaseLabel = (value: string) => {
  return value
    .replace(/[_-]/g, ' ') // replace _ and - with space
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
/**
 * Create a small subtitle, used for Entity Names.
 */
function SmallSubTitle({ name, icon, display = 'block' }) {
  return (
    <span
      style={{
        fontSize: 12,
        display,
        alignItems: 'center',
        fontWeight: 600,
        color: 'darkmagenta',
      }}>
      {icon}&nbsp;{name}
    </span>
  );
}

const skeleton = <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />;

/**
 * Gets the company details from the server and shows the
 * country and the primary type of the company.
 */
function ShowCompanyDetails({ id }: { id: string }) {
  const { loading, data: company, error } = useCompanyLoader(id);
  const companyType = company && company.company_types[0]?.name;
  return (
    <>
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
          {skeleton}
          {skeleton}
        </div>
      )}
      {!loading && company && (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', fontWeight: 500 }}>
          <SmallSubTitle
            name={company.hqcountry}
            icon={<LanguageIcon style={{ fontSize: '1em' }} color="secondary" />}
          />
          {companyType && (
            <SmallSubTitle
              name={companyType}
              icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
            />
          )}
        </div>
      )}
    </>
  );
}

export const SingleBuyerSellerCell = ({
  name: entityName,
  vtrId: entityVtrId,
  // type: entityType= 'COMPANY',
  white = false,
  isMapping = false,
}: {
  name: React.ReactNode;
  vtrId?: string;
  // type: string;
  white?: boolean;
  // contentType?: any; //ignore
  isMapping?: boolean;
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  let Details: any;
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_URL}/${entityVtrId}.png`,
      '/images/company_default.svg',
    ].filter(e => e);
  }, []);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/company_default.svg',
    useSuspense: false,
  });

  Details = ShowCompanyDetails;
  return entityName || entityVtrId ? (
    <>
      <EntityNameColumn
        name={entityName}
        type={'COMPANY'}
        vtrId={entityVtrId}
        secondary={
          entityVtrId ? (
            Details ? (
              <Details id={entityVtrId} />
            ) : (
              <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
            )
          ) : null
        }
        avatarImg={fallbackedSource}
        secondaryTypographyProps={secondaryProps}
      />
    </>
  ) : null;
};

export const FIELD_MAP: Record<string, string> = {
  'Company Name': 'vtr_company_name',
  /* 'Company Also Known As': 'company_aka',
  'Company Legal Name': 'vtr_company_legal_name',
  'Company Former Name': 'company_former_name', */
  'Company Type': 'vtr_company_type_id',
  'Company Service': 'vtr_company_services_id',
  'Company Description': 'company_description',
  'HQ Country': 'hqcountry',
  'Company Founding Year': 'company_founding_year',
  'Group Company Name': 'group_company_name',
  'IMDB_Company ID': 'imdb_company_id',
};

const OPERATOR_MAP: Record<string, string> = {
  vtr_company_name: /* "$eq", // */ '$contains',
  company_aka: '$contains',
  vtr_company_legal_name: '$contains',
  company_former_name: '$contains',
  company_description: '$contains',
  imdb_company_id: '$eq',
  hqcountry: '$in',
  vtr_company_type_id: '$in',
  vtr_company_services_id: '$in',
  group_company_name: '$contains',
  company_founding_year: '$in',
};

const NAME_GROUP_FIELDS = [
  'vtr_company_name',
  'company_aka',
  'company_former_name',
  'vtr_company_legal_name',
];

const normalize = (val: any) => {
  if (!val) return '';
  return String(val)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

export function getMatchedCompanyType(
  companyType: string = '',
  masterTypes: { id: number; type: string }[] = [],
): { id: number; label: string } | null {
  if (!companyType || !masterTypes.length) return null;
  const normalizedCompanyType = normalize(companyType);

  const match = masterTypes.find(m => normalize(m.type) === normalizedCompanyType);

  return match ? { id: match.id, label: match.type } : null;
}

export function getMatchedServices(
  clientServices: (string | { service: string })[] = [],
  masterServices: { id: number; service: string }[] = [],
): { id: number; service: string }[] {
  if (!clientServices.length || !masterServices.length) return [];

  const normalizedClient = clientServices
    .map(cs => normalize(typeof cs === 'string' ? cs : cs.service))
    .filter(Boolean);

  return masterServices
    .filter(ms => normalizedClient.includes(normalize(ms.service)))
    .map(ms => ({
      id: ms.id,
      service: ms.service,
    }));
}

const mergeDynamicColumns = (row: Record<string, any>) => {
  const mergedRow = { ...row };

  const keys = Object.keys(row);

  // 🔹 Merge Company Types
  const typeValues = keys
    .filter(key => key.toLowerCase().startsWith('company type_'))
    .map(key => row[key])
    .filter(Boolean);

  if (typeValues.length) {
    mergedRow['Company Type'] = typeValues.join(',');
  }

  // 🔹 Merge Company Services (plural)
  const serviceValues = keys
    .filter(key => key.toLowerCase().startsWith('company services_'))
    .map(key => row[key])
    .filter(Boolean);

  if (serviceValues.length) {
    mergedRow['Company Service'] = serviceValues.join(',');
  }

  return mergedRow;
};

export const buildBackendPayload = (
  row: Record<string, any>,
  selectedFields: string[],
  masterTypes: any[],
  masterServices: any[],
  page = 1,
  pageSize = 10,
): Record<string, any> => {
  // ✅ Normalize new excel format into old structure
  const normalizedRow = mergeDynamicColumns(row);
  const payload: Record<string, any> = {
    page,
    page_size: pageSize,
  };

  selectedFields.forEach(backendField => {
    // 🔹 Map AKA / Former / Legal → vtr_company_name
    /*   const effectiveField = NAME_GROUP_FIELDS.includes(backendField)
    ? "vtr_company_name"
    : backendField; */
    const effectiveField = FIELD_MAP[backendField] || backendField; // Map back to backend field names
    const label = Object.keys(FIELD_MAP).find(key => FIELD_MAP[key] === effectiveField);

    if (!label) return;
    const rawValue = normalizedRow[label];
    if (!rawValue) return;

    if (effectiveField === 'vtr_company_type_id') {
      const typeArray = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

      const matchedTypes = typeArray
        .map((type: string) => getMatchedCompanyType(type.trim(), masterTypes))
        .filter(Boolean);

      if (matchedTypes.length) {
        payload[effectiveField] = {
          $in: matchedTypes.map(t => `${t!.id}|||${t!.label}`).join(','),
        };
      }

      return;
    }

    if (effectiveField === 'vtr_company_services_id') {
      const servicesArray = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

      const matchedServices = getMatchedServices(servicesArray, masterServices);

      if (matchedServices.length) {
        payload[effectiveField] = {
          $in: matchedServices.map(s => `${s.id}|||${s.service}`).join(','),
        };
      }

      return;
    }
    // 🔹 DEFAULT FIELDS
    const operator = OPERATOR_MAP[effectiveField] || '$contains';
    /* payload[effectiveField] = { [operator]: String(rawValue) };
  }); */

    if (operator === '$in') {
      const values =
        typeof rawValue === 'string'
          ? rawValue
              .split(',')
              .map(v => v.trim())
              .filter(Boolean)
              .join(',')
          : rawValue;

      payload[effectiveField] = {
        $in: values,
      };
    } else {
      payload[effectiveField] = {
        [operator]: String(rawValue),
      };
    }
  });

  return payload;
};

export const CellForCompEntity = () => (associated_comp: any) => {
  const RenderComponent = SingleBuyerSellerCell;

  if (!associated_comp?.vtr_company_name && !associated_comp?.vtr_id) {
    return null;
  }

  const firstEntityName = associated_comp?.vtr_company_name;
  return (
    <>
      <RenderComponent
        name={<>{firstEntityName} </>}
        vtrId={associated_comp?.vtr_id}
        isMapping={true}
      />
    </>
  );
};
